import {
  CSSProperties,
  ReactNode,
  ComponentProps,
  forwardRef,
  useState,
  useEffect,
} from "react";
import { UseFormRegisterReturn } from "react-hook-form";
import styles from "./Input.module.css";
import Label from "../Label";
import FieldError from "components/FieldError/FieldError";
import FormFieldWrapper from "components/FormFieldWrapper/FormFieldWrapper";

type TextInputProps = Omit<ComponentProps<"input">, "ref"> & {
  label?: string;
  register?: UseFormRegisterReturn;
  error?: string;
  required?: boolean;
  hasTooltip?: boolean;
  content?: string;
  hasLabel?: boolean;
  containerStyle?: CSSProperties;
  errorSpanStyle?: CSSProperties;
  underDevelopment?: boolean;
  decimalPlaces?: number;
  labelAction?: ReactNode;
  inputRef?: React.RefObject<HTMLInputElement>;
};

const TextInput = forwardRef<HTMLInputElement, TextInputProps>(({
  label,
  register,
  error,
  required,
  hasTooltip,
  containerStyle,
  errorSpanStyle,
  content,
  hasLabel = true,
  underDevelopment,
  placeholder,
  decimalPlaces,
  labelAction,
  inputRef,
  ...props
}, forwardedRef) => {
  // Live value for the title attribute (works with both register and Controller)
  const [liveTitle, setLiveTitle] = useState(() =>
    String(props.value ?? props.defaultValue ?? "")
  );

  // Sync title when Controller (or a parent) passes a new value prop
  useEffect(() => {
    setLiveTitle(String(props.value ?? props.defaultValue ?? ""));
  }, [props.value, props.defaultValue]);

  // Pull ref out of register so we can call it manually and avoid JSX ref collisions
  const { ref: registerRef, ...registerRest } = register || {};

  const handleInput = (e: React.FormEvent<HTMLInputElement>) => {
    const { value } = e.currentTarget;

    // Keep title live while typing (covers register/ uncontrolled usage)
    setLiveTitle(value);

    if (!decimalPlaces) return;

    const regex = new RegExp(`^(\\d+)?(\\.\\d{0,${decimalPlaces}})?$`);
    if (!regex.test(value)) {
      const trimmed = value.slice(0, -1);
      e.currentTarget.value = trimmed;
      setLiveTitle(trimmed);
    }
  };

  const renderLabel = () => {
    if (!hasLabel) return null;
    return (
      <Label
        required={required}
        tooltipContent={content}
        underDevelopment={underDevelopment}
      >
        {label}
      </Label>
    );
  };

  return (
    <FormFieldWrapper
      label={renderLabel()}
      error={<FieldError error={error} />}
      containerStyle={containerStyle}
      labelAction={labelAction}
    >
      <input
        {...registerRest}
        {...props}
        ref={(element) => {
          // 1. React-Hook-Form register ref
          if (typeof registerRef === "function") {
            registerRef(element);
          }

          // 2. Controller / forwardRef
          if (typeof forwardedRef === "function") {
            forwardedRef(element);
          } else if (forwardedRef) {
            (forwardedRef as React.MutableRefObject<HTMLInputElement | null>).current = element;
          }

          // 3. Legacy custom inputRef prop
          if (inputRef) {
            (inputRef as React.MutableRefObject<HTMLInputElement | null>).current = element;
          }
        }}
        className={styles["form__input"]}
        placeholder={props.disabled ? "" : placeholder || label}
        title={liveTitle}
        onInput={(e) => {
          handleInput(e);
          props.onInput?.(e);
        }}
      />
    </FormFieldWrapper>
  );
});

TextInput.displayName = "TextInput";
export default TextInput;